<template>
  <div class="ofs-sidebar ofs__content">
    <div class="ofs__block ofs-sidebar__tree">
      <UIPreloader v-if="!initialized" message="Loading tree..." class="ofs__preloader" />
      <UITree
        v-else-if="data.tree"
        :data="data.tree"
        :root="AnalyticsField.REPO_ROOT"
        :meta-fields="METADATA_FIELDS"
        :formatter="nameFormatter"
        unsearchable
      >
        <template #name="{ isRoot, name, path, open }">
          <draggable
            :group="isRoot ? { name: 'nodes', put: true, pull: false } : 'nodes'"
            :model-value="isRoot ? [repoName] : [path]"
            tag="div"
            :animation="0"
            :itemKey="(element: string | string[]) => isRoot ? element : (element as string[]).join('-')"
            @choose="handleChoose(path)"
            @change="handleChange($event, path, open)"
          >
            <template #header><div class="ofs-sidebar__tree-item-placeholder">{{ name }}</div></template>
            <template #item><div class="ofs-sidebar__tree-item-name">{{ isRoot ? repoName : name }}</div></template>
          </draggable>
        </template>
        <template #meta="{ isRoot, amount, path }">
          <AmountField
            v-if="!isRoot"
            :entry-settings
            :amount
            :dynamic="path.length > 2"
            @click="handleClickAmount(path)"
          />
        </template>
        <template #actions="{ isRoot }">
          <div v-if="isRoot" class="ofs-sidebar__tree-actions">
            <UIButton
              v-tooltip.top="'Undo'"
              variant="gray"
              fill="light"
              size="xs"
              :leading="ArrowUturnLeftIcon"
              :disabled="!canUndo"
              @click="handleUndo"
            />
            <UIButton
              v-tooltip.top="'Redo'"
              variant="gray"
              fill="light"
              size="xs"
              :leading="ArrowUturnRightIcon"
              :disabled="!canRedo"
              @click="handleRedo"
            />
          </div>
        </template>
      </UITree>
    </div>
    <div class="ofs__block ofs-sidebar__unorganized">
      <div class="ofs__header">
        <span>Unorganized owners/groups:</span>
        <UIButton
          label="Owner/Group"
          variant="secondary"
          size="xs"
          :leading="PlusIcon"
          :disabled="!initialized"
          @click="handleAddGroup"
        />
      </div>
      <UIPreloader v-if="!initialized" message="Loading tree..." class="ofs__preloader" />
      <TransitionRoot v-else as="div" class="ofs-sidebar__unorganized-area">
        <draggable
          group="nodes"
          :model-value="data.orphans"
          tag="div" 
          :animation="0"
          :itemKey="(element: string) => element"
          class="ofs-sidebar__unorganized-list"
          @change="handleOrphansChange"
        >
          <template #header>
            <UILayoutNoData v-if="!data.orphans.length" hide-question-icon inline />
            <div class="ofs-sidebar__unorganized-remove-area">Move here to remove tree node</div>
          </template>
          <template #item="{ element }">
            <TransitionChild
              as="template"
              enter="transition-transform ease-linear duration-300 origin-center"
              enter-from="scale-0 opacity-0"
              enter-to="scale-100 opacity-100"
            >
              <OFSSidebarOrphan :element />
            </TransitionChild>
          </template>
        </draggable>
      </TransitionRoot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, markRaw, inject, watch, nextTick } from 'vue'
import { cloneDeep, get, has, set, unset } from 'lodash'
import draggable from 'vuedraggable'
import { useManualRefHistory } from '@vueuse/core'

import { AnalyticsField, TransactionSettings } from '@types'
import { OrganizeFamilyStructureItem, OrganizeFamilyStructurePath } from './utils/types'

import { METADATA_FIELDS } from './utils/const'

import { onCreateAsset } from '@/entities/transactions/components/utils/helpers'

import { useNotifications } from '@/plugins/notification'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories'

import { UILayoutNoData, UIPreloader, UITree } from '@ui/core'
import { UIButton } from '@ui/buttons'
import { ArrowUturnLeftIcon, ArrowUturnRightIcon, PlusIcon } from '@heroicons/vue/24/outline'

import PrecisionForm from './components/PrecisionForm.vue'
import AmountField from './components/AmountField.vue'
import OFSSidebarOrphan from './OrganizeFamilyStructureSidebarOrphan.vue'
import { TransitionChild, TransitionRoot } from '@headlessui/vue'

type OFSData = { tree: Record<string, OrganizeFamilyStructureItem>; orphans: string[] }

type Props = {
  paths: OrganizeFamilyStructurePath[]
  entrySettings?: TransactionSettings['entries'][number]
  accountTypes: string[]
  initialized: boolean
}

const props = defineProps<Props>()

const data = defineModel<OFSData>('data', { required: true })

const { error, warn } = useNotifications()

const assetsBunchStore = useAssetsBunchStore()
const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()

const { undo, redo, canUndo, canRedo, clear, commit } = useManualRefHistory(data, {
  dump: JSON.stringify,
  parse: JSON.parse
})

const nameFormatter = inject<(name: string) => string>('nameFormatter')

const chosenPath = ref<string[]>()

let historyCommitVersion = 0
let isHistoryCommitPending = false

const repoName = computed(() => repositoriesStore.getCurrentName)

const cancelPendingHistoryCommit = () => {
  historyCommitVersion += 1
  isHistoryCommitPending = false
}

const commitHistory = () => {
  if (!props.initialized || isHistoryCommitPending) return
  isHistoryCommitPending = true
  const version = ++historyCommitVersion

  nextTick(() => {
    if (version !== historyCommitVersion) return
    isHistoryCommitPending = false
    commit()
  })
}

const setHistoryBaseline = () => {
  cancelPendingHistoryCommit()
  commit()
  clear()
}

defineExpose({
  commitHistory,
})

const handleUndo = () => {
  cancelPendingHistoryCommit()
  undo()
}

const handleRedo = () => {
  cancelPendingHistoryCommit()
  redo()
}

// handle choose node in tree
const handleChoose = (path: string[]) => {
  chosenPath.value = path
}

// handle change in tree
const handleChange = (event: any, path: string[], open: () => void) => {
  if (event.added) {
    const element = event.added.element
    const assetId = Array.isArray(element) ? element.at(-1) : element
    handleAddNode(path, assetId)
    open()
  } else if (event.removed) {
    handleRemovedNode(event.removed.element)
  }
}

// handle add node to tree
const handleAddNode = (path: string[], assetId: string) => {
  const tree = cloneDeep(data.value.tree)
  const targetNode = get(tree, [...path, assetId])
  let amount = (targetNode?.amount ?? 0)
  if (chosenPath.value) {
    // get asset id and account id
    const accountId = path.at(-1)
    if (!assetId || !accountId) {
      error({ message: 'Asset ID and account ID are required' })
      return
    }
    if (assetId === accountId) {
      error({ message: 'You cannot add a node to itself' })
      chosenPath.value = undefined
      return
    }
    const chosenNode = get(tree, chosenPath.value)
    if (!chosenNode) return
    amount += (chosenNode.amount ?? 0)
  } else {
    // if orphan node is added, set amount to 1
    amount = 1
  }
  // if target exists, sum amounts
  const newPath = [...path, assetId]
  set(tree, newPath, {
    ...targetNode,
    path: newPath,
    amount,
  })
  data.value.tree = tree
  commitHistory()
  // if orphan node is added, check precision amount
  if (!chosenPath.value && path.length > 1) {
    nextTick(() => {
      checkPrecisionAmount(assetId)
    })
  }
}

// handle removed node from tree
const handleRemovedNode = (path: string[]) => {
  if (!chosenPath.value) return
  const tree = cloneDeep(data.value.tree)
  chosenPath.value = undefined
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  unset(tree, path)
  data.value.tree = tree
  commitHistory()
}

const isPathWithinPath = (path: string[], targetPath: string[]) => {
  return targetPath.every((item, index) => path[index] === item)
}

// Collect the removed node and every nested child asset from the current tree branch.
const getNodeAssetIds = (node: OrganizeFamilyStructureItem, path: string[]) => {
  const assetIds: string[] = []
  const assetId = path.at(-1)
  if (assetId) {
    assetIds.push(assetId)
  }

  Object.entries(node).forEach(([key, value]) => {
    if (METADATA_FIELDS.includes(key) || !value || typeof value !== 'object' || Array.isArray(value)) return
    assetIds.push(...getNodeAssetIds(value as OrganizeFamilyStructureItem, [...path, key]))
  })

  return assetIds
}

// Assets used in another branch should stay organized there and not be added to orphans.
const hasAssetOutsidePath = (assetId: string, path: string[]) => {
  return props.paths.some(item => item.path.at(-1) === assetId && !isPathWithinPath(item.path, path))
}

// if orphan asset was moved from unorganized to tree
const handleOrphansChange = (event: any) => {
  chosenPath.value = undefined
  const tree = cloneDeep(data.value.tree)
  let orphans = cloneDeep(data.value.orphans)
  if (event.added) {
    const path = event.added.element
    const node = get(tree, path)
    const assetId = path.at(-1)
    if (!assetId || !node) return
    // Moving a parent to orphans removes its whole subtree, so orphan eligible descendants too.
    getNodeAssetIds(node, path).forEach(childAssetId => {
      // If this asset still exists elsewhere in the tree, it should not become unorganized.
      if (hasAssetOutsidePath(childAssetId, path) || orphans.includes(childAssetId)) {
        if (childAssetId === assetId && hasAssetOutsidePath(childAssetId, path)) {
          warn({ message: 'Asset is in few nodes, please remove it from all nodes if you want to make it unorganized' })
        }
        return
      }
      orphans.push(childAssetId)
    })
    // remove asset from tree by current path
    unset(tree, path)
  } else if (event.removed) {
    const assetId = event.removed.element
    // remove asset id from orphans
    orphans = orphans.filter(id => id !== assetId)
  }
  data.value.tree = tree
  data.value.orphans = orphans
  commitHistory()
}

const handleClickAmount = (path: string[]) => {
  // get asset id and account id
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  checkPrecisionAmount(assetId)
}

const handleAddGroup = () => {
  const restrictions = props.accountTypes
  const instance = assetsBunchStore.createElement()
  onCreateAsset(
    '',
    { restrictions, hideEODSearch: true, equalizeTicker: true },
    instance,
    modalsStore,
    (assetId: string) => {
      const orphans = cloneDeep(data.value.orphans)
      orphans.unshift(assetId)
      data.value.orphans = orphans
      commitHistory()
    }
  )
}

const checkPrecisionAmount = (assetId: string) => {
  // open amount form
  const modalInstance = modalsStore.init('precision-form', markRaw(PrecisionForm))
  modalInstance?.open(modalsStore.getZIndex(), {
    entrySettings: props.entrySettings,
    assetId,
    paths: props.paths,
    onComplete: (paths: OrganizeFamilyStructurePath[]) => {
      paths.forEach(item => {
        const assetId = item.path.at(-1)
        const accountId = item.path.at(-2)
        if (!assetId || !accountId) return
        const tree = cloneDeep(data.value.tree)
        if (has(tree, item.path)) {
          set(tree, [...item.path, 'amount'], item.amount)
        } else {
          set(tree, item.path, {
            path: item.path,
            amount: item.amount,
          })
        }
        data.value.tree = tree
      })
      commitHistory()
    }
  })
}

watch(() => props.initialized, (value, prev) => {
  if (value && !prev) {
    setHistoryBaseline()
  }
}, { immediate: true })
</script>

<style scoped>
.ofs-sidebar {
  &__tree {
    @apply pt-1 sm:pt-2;

    :deep(.ui-tree) {
      @apply h-full;
    }

    :deep(.ui-tree__name) {
      @apply relative;
      @apply flex-auto;
    }

    :deep(.ui-tree__item) {
      @apply min-h-10;
      @apply justify-between;
      @apply pl-0.5 pr-1;
      @apply bg-body-gray hover:bg-body;

      &:has(.sortable-ghost) {
        @apply bg-indigo-50 dark:bg-gray-900;
      }

      &:has(.sortable-ghost) {
        .ofs-sidebar__tree-item-placeholder {
          @apply block;
        }
        .ofs-sidebar__tree-item-name {
          @apply hidden;
        }
        .ofs-sidebar-orphan {
          @apply hidden;
        }
      }
    }
  }

  &__tree-actions {
    @apply flex items-center gap-2;
  }

  &__tree-item-placeholder {
    @apply hidden last:block;
    @apply last:opacity-50;
  }

  &__tree-item-name {
    @apply cursor-move;
  }

  &__unorganized {
    @apply flex flex-col gap-4;
  }

  &__unorganized-area {
    @apply relative;
    @apply overflow-y-auto;
  }

  &__unorganized-list {
    @apply flex-auto flex flex-col gap-1.5;

    .no-data {
      @apply flex flex-auto items-center justify-center;
    }
  }

  &__unorganized-remove-area {
    @apply hidden items-center justify-center;
    @apply py-1.5 px-2.5 gap-2;
    @apply rounded-md;
    @apply border border-dashed border-red-500 dark:border-red-300;
    @apply text-sm text-red-700 dark:text-red-300;
  }

  &:has(&__tree .sortable-ghost) &__unorganized-remove-area {
    @apply flex;
  }
}
</style>
